import { html } from 'lit';
import { TagKey } from '@cof/tag-catalog-fs-personal-mfe';
import { ApplicationService } from '../../../../services/application.service.js';
import { style } from './address-v2.style.js';
import { REFI_PERSONAL_CONST } from '../../../../shared/constants/app.constants.js';
import { currencyFormatter } from '../../../../shared/formatters/currency.formatter.js';
import { globalStyle } from '../../../../global-styles.style.js';
import '@cof/fs-select-wc/fs-select.js';
import '../../../edit-card/edit-card.component.js';
import '../net-disposable-income/address-ndi-v2.component.js';
import { validateForm } from '../../../../shared/decorators/validate-form.decorator.js';
import { stratum } from '../../../../../constants/stratum.js';
import { IconicV2BaseComponent } from '../../../iconic-v2-base/iconic-v2-base.component.js';

class RefiPersonalAddressComponent extends IconicV2BaseComponent {
  constructor() {
    super();
    this.values = {};
    this.coAppPrefix = '';
    this.aptPattern = '^([a-zA-Z0-9]| |-|\\.)*$';
    this.displayError = false;
    this.isPrefill = false;
    this.validity = {};
    this.applicantType = 'primaryApplicant';
    this.elementIds = [];
  }

  static styles = [style, globalStyle];

  static properties = {
    displayError: { type: Boolean, attribute: 'display-error', reflect: true },
    applicantType: { type: String, attribute: 'applicant-type', reflect: true },
    confirmationPage: {
      type: Boolean,
      reflect: true,
      attribute: 'confirmation-page',
    },
    mockApplication: {
      type: Boolean,
      reflect: true,
      attribute: 'mock-application',
    },
    inactive: { type: Boolean, attribute: 'inactive', reflect: true },
  };

  connectedCallback() {
    super.connectedCallback();
    this.setValues();
    this.resetValues();

    this.renderedNonFinancial = false;
    this.coAppPrefix = !(this.applicantType === 'coApplicant') ? '' : 'co-';
    // Element IDs
    this.streetId = `${this.coAppPrefix}street`;
    this.aptId = `${this.coAppPrefix}aptNumber`;
    this.cityId = `${this.coAppPrefix}city`;
    this.stateCodeId = `${this.coAppPrefix}stateCode`;
    this.zipId = `${this.coAppPrefix}zipCode`;
    this.ndiFieldsChildId = `${this.coAppPrefix}ndiFields`;

    if (
      ApplicationService.Application.profileType ===
        REFI_PERSONAL_CONST.FINANCIAL &&
      ApplicationService.Application.primaryApplicant.currentAddress.street &&
      ApplicationService.Application.primaryApplicant.currentAddress.city &&
      ApplicationService.Application.primaryApplicant.currentAddress
        .stateCode &&
      ApplicationService.Application.primaryApplicant.currentAddress.zipCode
    ) {
      this.isPrefill = true;

      // Fire PQ_TEXT_INPUT_FIELD_PREFILLED_FIELD tag
      if (!this.confirmationPage) {
        ['street', 'aptNumber', 'city', 'stateCode', 'zipCode'].forEach(
          (field) => {
            if (
              field !== 'aptNumber' ||
              (field === 'aptNumber' &&
                ApplicationService.Application.primaryApplicant.currentAddress
                  .aptNumber)
            ) {
              stratum.publishTag(TagKey.PQ_TEXT_INPUT_FIELD_PREFILLED_FIELD, {
                replacements: { value: field },
              });
            }
          }
        );
      }
    }

    this.elementIds = [
      this.streetId,
      this.aptId,
      this.cityId,
      this.stateCodeId,
      this.zipId,
    ];

    if (this.applicantType === 'coApplicant' || this.confirmationPage) {
      this.elementIds.push(this.ndiFieldsChildId);
      this.ndiFieldsChild = this.shadowRoot.getElementById(
        this.ndiFieldsChildId
      );
    }
  }

  firstUpdated() {
    super.firstUpdated();
  }

  setValues() {
    const address =
      ApplicationService.Application[this.applicantType].currentAddress;
    this.values = {
      aptNumber: address.aptNumber ?? '',
      city: address.city ?? '',
      monthlyPayment: address.monthlyPayment ?? '',
      ownerStatus: address.ownerStatus ?? '',
      stateCode: address.stateCode ?? '',
      street: address.street ?? '',
      zipCode: address.zipCode ?? '',
    };
  }

  resetValues() {
    this.setValues();
    this.debug.log(`${this.coAppPrefix}Values: ${JSON.stringify(this.values)}`);

    setTimeout(() => {
      this.elementIds.forEach((id) => {
        if (id !== `${this.coAppPrefix}ndiFields`) {
          const filteredName = id.replace(this.coAppPrefix, '');
          const field = this.shadowRoot.getElementById(id);
          field.setAttribute('value', this.values[filteredName]);

          this.dispatchEvent(
            new CustomEvent('validity', {
              detail: {
                name: id,
                isValid: field.validity.valid,
              },
            })
          );
        }
      });
      // Reset NDI Fields
      if (this.applicantType === 'coApplicant' || this.confirmationPage) {
        this.ndiFieldsChild = this.shadowRoot.getElementById(
          this.ndiFieldsChildId
        );
        this.ndiFieldsChild.resetValues();
      }
    }, 0);

    this.requestUpdate();
  }

  render() {
    if (
      (this.applicantType === 'primaryApplicant' && this.isPrefill) ||
      this.confirmationPage
    ) {
      // Edit card rendered only for financial - primary
      return html`${this.renderCardForm()}`;
    }
    return html`${this.renderFields()}`;
  }

  getDisplayText() {
    const displayText = [
      `${this.streetDisplayText()}`,
      `${ApplicationService.Application[this.applicantType].currentAddress.city} ${
        ApplicationService.Application[this.applicantType].currentAddress
          .stateCode
      } ${ApplicationService.Application[this.applicantType].currentAddress.zipCode}`,
    ];

    if (this.applicantType === 'coApplicant' || this.confirmationPage) {
      let { ownerStatus } =
        ApplicationService.Application[this.applicantType].currentAddress;
      // Convert ownerStatus to only have the first letter capitalized
      ownerStatus = ownerStatus
        ? ownerStatus.toString().charAt(0).toUpperCase() +
          ownerStatus.toString().slice(1).toLowerCase()
        : '';

      const { monthlyPayment } =
        ApplicationService.Application[this.applicantType].currentAddress;
      const formattedPayment =
        monthlyPayment !== ''
          ? currencyFormatter(Math.round(monthlyPayment))
          : '';

      displayText.push(`${ownerStatus}`);
      displayText.push(`${formattedPayment}/monthly`);
    }

    return displayText;
  }

  renderCardForm() {
    if (!this.renderedNonFinancial && this.applicantType === 'coApplicant') {
      this.renderedNonFinancial = true;
      stratum.publishTag(TagKey.VIEW_COBORROWER_ADDRESS_REVIEW);
    }
    if (
      !this.renderedNonFinancial &&
      this.applicantType === 'primaryApplicant'
    ) {
      this.renderedNonFinancial = true;
      stratum.publishTag(TagKey.VIEW_ADDRESS_REVIEW);
    }
    return html` <refi-personal-edit-card
      card-title="Address"
      component-id="${this.coAppPrefix}address-edit-card"
      id="${this.coAppPrefix}address-edit-card"
      card-icon="fs-24-home"
      .displayText="${this.getDisplayText()}"
      ?inactive="${this.inactive}"
      ?show-error="${this.displayError}"
      @edit-clicked="${this.editClicked}"
      @save-clicked="${this.saveClicked}"
      @cancel-clicked="${this.cancelClicked}"
    >
      <form
        autocomplete="off"
        novalidate
        @submit="${this.onSubmit}"
        id="${this.coAppPrefix}address-edit-card-form"
      >
        ${this.renderFields()}
      </form>
    </refi-personal-edit-card>`;
  }

  streetDisplayText() {
    let streetDisplayText = '';
    if (ApplicationService.Application[this.applicantType]) {
      if (
        ApplicationService.Application[this.applicantType].currentAddress
          .street &&
        ApplicationService.Application[this.applicantType].currentAddress
          .aptNumber
      ) {
        streetDisplayText = `${
          ApplicationService.Application[this.applicantType].currentAddress
            .street
        } ${ApplicationService.Application[this.applicantType].currentAddress.aptNumber}`;
      } else {
        streetDisplayText = `${
          ApplicationService.Application[this.applicantType].currentAddress
            .street
        }`;
      }
    }
    return streetDisplayText;
  }

  editClicked() {
    // Send event to step that the card is open
    this.dispatchEvent(
      new CustomEvent('edit-card-open', {
        bubbles: true,
        composed: true,
        detail: {
          component: `${this.coAppPrefix}refi-address-v2`,
        },
      })
    );
    stratum.publishTag(TagKey.PQ_CONFIRMATION_EDIT_CLICKED, {
      replacements: {
        value: `${
          this.applicantType === 'primaryApplicant' ? '' : 'co borrower '
        }address`,
      },
    });
    if (this.applicantType === 'coApplicant') {
      stratum.publishTag(TagKey.VIEW_COBORROWER_ADDRESS_EDIT);
    } else {
      stratum.publishTag(TagKey.VIEW_ADDRESS_EDIT);
    }
  }

  saveClicked() {
    // Trigger form submission in card
    const form = this.shadowRoot.getElementById(
      `${this.coAppPrefix}address-edit-card-form`
    );
    form.requestSubmit();

    stratum.publishTag(TagKey.PQ_CONFIRMATION_SAVE_CLICKED, {
      replacements: {
        value: `${
          this.applicantType === 'primaryApplicant' ? '' : 'co borrower '
        }address`,
      },
    });
  }

  cancelClicked() {
    // Reset fields to original values (from Application)
    this.displayError = false;
    this.validity = {};
    this.resetValues();
    this.dispatchEvent(
      new CustomEvent('edit-card-closed', {
        bubbles: true,
        composed: true,
        detail: {
          component: `${this.coAppPrefix}refi-address-v2`,
        },
      })
    );
    stratum.publishTag(TagKey.PQ_CONFIRMATION_CANCEL_CLICKED, {
      replacements: {
        value: `${
          this.applicantType === 'primaryApplicant' ? '' : 'co borrower '
        }address`,
      },
    });
  }

  @validateForm()
  async onSubmit() {
    // Principal applicant address card
    // Check validity, update Application, then close editing card
    let valid = true;

    this.runBusinessChecks();

    this.debug.log('validity: ', this.validity);
    Object.values(this.validity).forEach((validityValue) => {
      if (!validityValue) {
        valid = false;
      }
    });

    this.debug.log(
      'Principal applicant address-card valid and ready to save?',
      valid
    );
    if (valid) {
      this.saveToApplication();
      const cardComponent = this.shadowRoot.getElementById(
        `${this.coAppPrefix}address-edit-card`
      );
      cardComponent?.closeEditing();
      this.displayError = false;
      this.dispatchEvent(
        new CustomEvent('edit-card-closed', {
          bubbles: true,
          composed: true,
          detail: {
            component: `${this.coAppPrefix}refi-address-v2`,
          },
        })
      );
    }
  }

  saveToApplication() {
    // Keeping track of old values for PQ_TEXT_INPUT_FIELD_CHANGED_FIELD tag
    this.oldAddress = JSON.parse(
      JSON.stringify(
        ApplicationService.Application[this.applicantType].currentAddress
      )
    );

    // Collect all updates in a single object
    const updates = {};

    this.elementIds.forEach((_id) => {
      if (_id === this.ndiFieldsChildId) {
        this.shadowRoot
          .getElementById(this.ndiFieldsChildId)
          .saveToApplication();
      } else {
        const fieldName = _id.replace(this.coAppPrefix, '');
        updates[fieldName] = this.values[fieldName];
      }
    });

    // Update application once with all changes
    ApplicationService.Application = {
      ...ApplicationService.Application,
      [this.applicantType]: {
        ...ApplicationService.Application[this.applicantType],
        currentAddress: {
          ...ApplicationService.Application[this.applicantType].currentAddress,
          ...updates,
        },
      },
    };

    this.requestUpdate();
    this.tagFieldChangesOnSubmit();
    this.dispatchEvent(
      new CustomEvent('host-app-confirmation-page-event', {
        bubbles: true,
        composed: true,
        detail: {
          actionName: 'update-application-personal',
          data: { application: ApplicationService.Application },
        },
      })
    );
  }

  tagFieldChangesOnSubmit() {
    const { currentAddress } =
      ApplicationService.Application[this.applicantType];

    Object.keys(currentAddress).forEach((field) => {
      if (currentAddress[field] !== this.oldAddress[field]) {
        this.debug.log(`${this.coAppPrefix}${field}`);
        stratum.publishTag(TagKey.PQ_TEXT_INPUT_FIELD_CHANGED_FIELD, {
          replacements: {
            value: `${this.coAppPrefix}${field}`,
          },
        });
      }
    });
  }

  renderFields() {
    return html` <div class="address-v2-fields">
      <div class="personal_compact-address">
        <div class="personal__address_street">
          <fs-text-field
            class="includes-error-message-street"
            id="${this.streetId}"
            autocomplete="street-address"
            name="${this.streetId}"
            helper-text="Sorry, no P.O. Boxes"
            type="text"
            value-missing-message="Please enter a home address"
            maxLength="50"
            minLength="5"
            pattern=" *[a-zA-Z0-9]+(?: [a-zA-Z0-9.]+)+ *"
            required
            @change="${(ev) => this.changed(this.streetId, ev)}"
            @validity="${this.updateValidity}"
            value="${this.values.street}"
            pattern-mismatch-message="Please use valid characters and more than one word"
            @blur="${(ev) => {
              this.publishTagOnBlur(
                this.streetId,
                ev,
                'Please use valid characters and more than one word'
              );
            }}"
          >
            Home address
          </fs-text-field>
          <fs-body-text
            size="small"
            variant="feedback-negative"
            ?hidden="${this.hideError(this.streetId)}"
            id="${`${this.streetId}invalidAddress`}"
          >
            PO Box is not allowed
          </fs-body-text>
        </div>
        <div class="personal__address_apt">
          <fs-text-field
            id="${this.aptId}"
            autocomplete="address-line2"
            name="${this.aptId}"
            type="text"
            value-missing-message="Please use valid characters"
            maxLength="8"
            pattern="${this.aptPattern}"
            @change="${(ev) => this.changed(this.aptId, ev)}"
            @validity="${this.updateValidity}"
            @blur="${(ev) => {
              this.publishTagOnBlur(
                this.aptId,
                ev,
                'Please use valid characters'
              );
            }}"
            value="${this.values.aptNumber}"
            pattern-mismatch-message="Please use valid characters"
          >
            Suite/Apt #
          </fs-text-field>
        </div>
      </div>
      <div class="personal__city">
        <fs-text-field
          id="${this.cityId}"
          autocomplete="address-level2"
          name="${this.cityId}"
          type="text"
          pattern="[a-zA-Z][a-zA-Z ]+"
          value-missing-message="Please enter a city"
          maxLength="25"
          required
          @change="${(ev) => this.changed(this.cityId, ev)}"
          @validity="${this.updateValidity}"
          @blur="${(ev) => {
            this.publishTagOnBlur(
              this.cityId,
              ev,
              'Please use valid characters'
            );
          }}"
          value="${this.values.city}"
          pattern-mismatch-message="Please use valid characters"
        >
          City
        </fs-text-field>
      </div>
      <div class="personal_compact-address">
        <div class="personal__address_state">
          <fs-select
            id="${this.stateCodeId}"
            name="${this.stateCodeId}"
            @blur="${(ev) => {
              this.publishTagOnBlur(
                this.stateCodeId,
                ev,
                'This field is required'
              );
            }}"
            @change="${(ev) => {
              this.changed(this.stateCodeId, ev);
            }}"
            value="${this.values.stateCode}"
            @validity="${this.updateValidity}"
            required
          >
            <span slot="label">State</span>
            <option value="">Select state</option>
            ${REFI_PERSONAL_CONST.US_STATES.map(
              (state) =>
                html` <option value="${state.value}">${state.name}</option>`
            )}
          </fs-select>
          <fs-body-text
            size="small"
            variant="feedback-negative"
            ?hidden="${this.hideError(this.stateCodeId)}"
            id="${`${this.stateCodeId}-invalidAddress`}"
          >
            We're sorry. We do not offer auto refinancing in this state.
          </fs-body-text>
        </div>
        <div class="personal__address_zip">
          <fs-text-field
            id="${this.zipId}"
            autocomplete="postal-code"
            name="${this.zipId}"
            type="tel"
            value-missing-message="Please enter a valid zip code"
            maxLength="5"
            minLength="5"
            pattern="(?!20189|20521|11111|33333|66666|77777|88888|99999)[0-9]{5}"
            required
            @change="${(ev) => this.changed(this.zipId, ev)}"
            @validity="${this.updateValidity}"
            @blur="${(ev) => {
              this.publishTagOnBlur(
                this.zipId,
                ev,
                'Please enter a valid zip code'
              );
            }}"
            value="${this.values.zipCode}"
            pattern-mismatch-message="Please enter a valid zip code"
          >
            ZIP
          </fs-text-field>
        </div>
      </div>
      <div class="ndi-fields">${this.renderNdiFields()}</div>
    </div>`;
  }

  renderNdiFields() {
    if (this.applicantType === 'coApplicant' || this.confirmationPage) {
      return html`<refi-address-ndi-v2
        id="${this.ndiFieldsChildId}"
        @validity="${this.updateValidity}"
        ?is-primary=${this.applicantType === 'primaryApplicant'}
      >
      </refi-address-ndi-v2>`;
    }
    return html``;
  }

  hideError(id) {
    return this.validity[id] === undefined || this.validity[id] === true;
  }

  changed(_id, ev, _value = ev?.target?.value) {
    const filteredName = _id.replace(this.coAppPrefix, '');
    this.values[filteredName] = _value?.trim();
    if (ev && ev.target) {
      this.updateValidity({
        detail: {
          name: _id,
          isValid: ev.target?.validity?.valid,
        },
      });
    }

    const element = { id: _id, value: _value };
    this.validateField(element);
  }

  runBusinessChecks() {
    this.elementIds.forEach((_id) => {
      if (
        this.applicantType === 'coApplicant' &&
        _id === this.ndiFieldsChildId
      ) {
        this.shadowRoot
          .getElementById(this.ndiFieldsChildId)
          .runBusinessChecks();
      } else {
        const field = {
          id: _id,
          value: this.shadowRoot.getElementById(_id)?.value,
        };
        this.validateField(field);
      }
    });
  }

  validateField(element) {
    let valid = false;
    // Validate business rules
    if (element.id === this.streetId) {
      valid = this.isPoBox(element.value) === null;
    } else if (element.id === this.stateCodeId) {
      valid = !REFI_PERSONAL_CONST.INELIGIBLE_REFI_STATES.includes(
        element.value
      );
    } else {
      // Only validate street and state
      return;
    }
    this.updateValidity({
      detail: {
        name: element.id,
        isValid: valid,
      },
    });

    if (!valid) {
      this.publishTagOnInvalidField(element.id);
    }

    this.requestUpdate();
  }

  publishTagOnInvalidField(id) {
    let message;
    switch (id) {
      case this.streetId:
        message = 'PO Box is not allowed';
        break;
      case this.stateCodeId:
        message =
          "We're sorry. We do not offer auto refinancing in this state.";
        break;
      default:
        break;
    }

    if (message) {
      stratum.publishTag(
        TagKey.PQ_TEXT_INPUT_FIELD_FIELD_VALIDATION_ERROR_MESSAGE,
        {
          replacements: {
            value: id === message,
          },
        }
      );
    }
  }

  publishTagOnBlur(id, ev, message) {
    // Publish tag on blur
    if (ev.target.validity.valid) {
      stratum.publishTag(TagKey.PQ_TEXT_INPUT_FIELD_EDITED_FIELD, {
        replacements: { value: id },
      });
    } else {
      // when format validation fails
      stratum.publishTag(
        TagKey.PQ_TEXT_INPUT_FIELD_FIELD_VALIDATION_ERROR_MESSAGE,
        {
          replacements: {
            value: message,
          },
        }
      );
    }
  }

  updateValidity(ev) {
    this.validity[ev.detail.name] = ev.detail.isValid;
    this.dispatchEvent(
      new CustomEvent('validity', {
        detail: {
          name: ev.detail.name,
          isValid: ev.detail.isValid,
        },
      })
    );
    this.requestUpdate();
  }

  isPoBox(streetValue) {
    let tempVal = streetValue.replace(/[^a-zA-Z0-9]+/g, '');
    tempVal = tempVal.substring(tempVal.toUpperCase().indexOf('P'));

    const str1 =
      '^([Pp]([Oo][Ss][Tt])?\\s{0,10}\\.?\\s{0,10})([Oo]([fF][Ff][Ii][Cc][Ee])?\\s{0,10}?\\s{0,10})([Bb]([Oo])?[Xx])(.*)$';

    const str2 = '^(.*)([Pp])([Bb])([Xx])(.*)$';
    const str3 = '^(.*)([Pp][Oo][Ss][Tt]\\s*)([Oo][fF][Ff][Ii][Cc][Ee])(.*)$';
    const str4 = '^(.*)([Pp][Oo][Ss][Tt]\\s*)([Bb][Oo][Xx])(.*)$';
    const str5 = '^([Pp])([Oo])([Bb])(.*)$';
    return (
      tempVal.match(new RegExp(str1)) ||
      tempVal.match(new RegExp(str2)) ||
      tempVal.match(new RegExp(str3)) ||
      tempVal.match(new RegExp(str4)) ||
      tempVal.match(new RegExp(str5))
    );
  }
}

customElements.define('refi-address-v2', RefiPersonalAddressComponent);

// Defining it again so that we can import a separate instance for coapp on confirmation page
customElements.define(
  'co-refi-address-v2',
  class extends RefiPersonalAddressComponent {}
);
